import trim from 'lodash/trim';
import trimEnd from 'lodash/trimEnd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Button, Icon } from 'semantic-ui-react';

import { useComponentOptions } from '../../config/design.context';
import { bem } from '../../core/design/bem';
import './BackButton.scss';

const css = bem('BackButton');

type BackButtonOptions = {
  enabled?: boolean;
  className?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  forceBackUrl?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  iconProps?: any;
  inverted?: boolean;
  label?: string;
  variant?: 'button' | 'link' | 'square-icon';
  goToParentPageIfNested?: boolean;
};

type BackButtonProps = {
  className?: string;
};

const BackButton = (props: BackButtonProps): JSX.Element | null => {
  const { className, ...otherProps } = props;
  const history = useHistory();
  const {
    enabled,
    label,
    variant = 'button',
    inverted,
    iconProps,
    forceBackUrl,
    goToParentPageIfNested,
    ...buttonProps
  } = useComponentOptions('BackButton') as BackButtonOptions;
  const { t } = useTranslation();

  if (!enabled) return null;

  const onBack = (e: React.MouseEvent) => {
    e.preventDefault?.();
    e.stopPropagation?.();

    const path = trim(history.location.pathname, '/');
    if (goToParentPageIfNested && path.includes('/')) {
      history.push(trimEnd(history.location.pathname, '/').replace(/\/[^/]+$/, ''));
    } else if (forceBackUrl) {
      history.push(forceBackUrl);
    } else {
      history.goBack();
    }
  };

  if (variant === 'square-icon' || variant === 'link') {
    return (
      <a
        href={undefined}
        {...buttonProps}
        {...otherProps}
        title="back"
        className={css({ variant, inverted }).mix(className, buttonProps.className).toString()}
        onClick={onBack}
      >
        <Icon name="angle left" bordered={variant === 'square-icon'} {...iconProps} />{' '}
        {label || t('btn.back')}
      </a>
    );
  }

  return (
    <Button
      {...buttonProps}
      {...otherProps}
      className={css({ variant, inverted }).mix(className, buttonProps.className).toString()}
      onClick={onBack}
    >
      <Icon name="angle left" {...iconProps} /> {label || t('btn.back')}
    </Button>
  );
};

BackButton.defaultProps = {
  className: undefined,
};

export default BackButton;
